import { useEffect, useState } from "react";
import {
  deleteDishGalleryImage,
  fetchDishGalleryForAdmin,
  resolveDishGalleryImageUrl,
  saveDishGalleryToApi,
  uploadDishGalleryImage,
} from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";
import "./dishGalleryAdmin.css";

function DishGalleryAdmin() {
  const [gallery, setGallery] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [editingTitles, setEditingTitles] = useState({});
  const [previewUrls, setPreviewUrls] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadGalleryFromCache = () => {
      const savedGallery = localStorage.getItem("carpeDiemDishGallery");
      if (!savedGallery) {
        setGallery([]);
        return;
      }

      const parsed = safeJsonParse(savedGallery, []);
      if (isMounted) {
        setGallery(Array.isArray(parsed) ? parsed : []);
      }
    };

    const syncGalleryFromApi = async () => {
      try {
        const galleryItems = await fetchDishGalleryForAdmin();
        if (!isMounted) {
          return;
        }

        setGallery(Array.isArray(galleryItems) ? galleryItems : []);
        localStorage.setItem(
          "carpeDiemDishGallery",
          JSON.stringify(Array.isArray(galleryItems) ? galleryItems : []),
        );
      } catch (_error) {
        // Keep local cache.
      }
    };

    loadGalleryFromCache();
    syncGalleryFromApi();

    const handleStorageChange = (event) => {
      if (event.key === "carpeDiemDishGallery") {
        loadGalleryFromCache();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("carpe-content-changed", syncGalleryFromApi);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("carpe-content-changed", syncGalleryFromApi);
    };
  }, []);

  useEffect(() => {
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  useEffect(() => {
    setEditingTitles((current) => {
      const next = { ...current };
      const validKeys = new Set();

      gallery.forEach((item) => {
        const key = item.id || item.imageUrl;
        validKeys.add(key);
        if (!(key in next)) {
          next[key] = String(item.title || "").trim();
        }
      });

      Object.keys(next).forEach((key) => {
        if (!validKeys.has(key)) {
          delete next[key];
        }
      });

      return next;
    });
  }, [gallery]);

  const syncCache = (nextGallery) => {
    setGallery(nextGallery);
    localStorage.setItem("carpeDiemDishGallery", JSON.stringify(nextGallery));
    window.dispatchEvent(new Event("carpe-content-changed"));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setSelectedTitles(files.map(() => ""));
  };

  const handleSelectedTitleChange = (index, value) => {
    setSelectedTitles((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const uploadSelectedPhotos = async () => {
    if (selectedFiles.length === 0) {
      setMessage("Choisissez au moins une photo.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      let nextGallery = [...gallery];

      for (const [index, file] of selectedFiles.entries()) {
        const title = String(selectedTitles[index] || "").trim();
        const result = await uploadDishGalleryImage(file, title);
        nextGallery = Array.isArray(result.dishGallery)
          ? result.dishGallery
          : nextGallery;
      }

      syncCache(nextGallery);
      setSelectedFiles([]);
      setSelectedTitles([]);
      setMessage("Photos ajoutées avec succès.");
    } catch (error) {
      setMessage(error?.message || "Impossible de publier les photos.");
    } finally {
      setLoading(false);
    }
  };

  const removePhoto = async (imageUrl) => {
    if (!window.confirm("Supprimer cette photo ?")) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const result = await deleteDishGalleryImage(imageUrl);
      const nextGallery = Array.isArray(result?.dishGallery)
        ? result.dishGallery
        : gallery.filter((item) => item.imageUrl !== imageUrl);
      syncCache(nextGallery);
      setMessage("Photo supprimée.");
    } catch (error) {
      setMessage(error?.message || "Impossible de supprimer cette photo.");
    } finally {
      setLoading(false);
    }
  };

  const updatePhotoTitle = async (imageUrl, nextTitleValue) => {
    const nextTitle = String(nextTitleValue || "").trim();

    setLoading(true);
    setMessage("");

    try {
      const nextGallery = gallery.map((item) => {
        if (item?.imageUrl !== imageUrl) {
          return item;
        }

        return {
          ...item,
          title: nextTitle,
        };
      });

      const savedGallery = await saveDishGalleryToApi(nextGallery);
      syncCache(Array.isArray(savedGallery) ? savedGallery : nextGallery);
      setMessage("Titre mis à jour.");
    } catch (error) {
      setMessage(error?.message || "Impossible de modifier le titre.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dish-gallery-admin-page">
      <header className="dish-gallery-admin-header">
        <div>
          <h1>Photos des plats</h1>
          <p>
            Ajoute plusieurs photos en une fois, supprime-les manuellement et
            elles disparaissent automatiquement au bout de 180 jours.
          </p>
        </div>
      </header>

      <section className="dish-gallery-admin-panel">
        <div className="form-group">
          <label htmlFor="dish-gallery-files">Photos des plats</label>
          <input
            id="dish-gallery-files"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileChange}
          />
        </div>

        {previewUrls.length > 0 && (
          <div className="dish-gallery-admin-preview-grid">
            {previewUrls.map((previewUrl, index) => (
              <figure key={previewUrl} className="dish-gallery-admin-preview-card">
                <img
                  src={previewUrl}
                  alt={`Aperçu ${index + 1}`}
                />
                <figcaption>
                  <label htmlFor={`dish-gallery-preview-title-${index}`}>
                    Titre de la photo
                  </label>
                  <input
                    id={`dish-gallery-preview-title-${index}`}
                    type="text"
                    value={selectedTitles[index] || ""}
                    onChange={(event) =>
                      handleSelectedTitleChange(index, event.target.value)
                    }
                    placeholder="Ex: Tartare de boeuf du chef"
                    disabled={loading}
                  />
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        <div className="dish-gallery-admin-actions">
          <button type="button" className="btn-primary" onClick={uploadSelectedPhotos} disabled={loading}>
            {loading ? "Publication..." : "Ajouter les photos"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setSelectedFiles([]);
              setSelectedTitles([]);
            }}
            disabled={loading}
          >
            Réinitialiser
          </button>
        </div>

        {message && <p className="dish-gallery-admin-message">{message}</p>}
      </section>

      <section className="dish-gallery-admin-grid">
        {gallery.length === 0 ? (
          <div className="dish-gallery-admin-empty">
            Aucune photo publiée.
          </div>
        ) : (
          gallery.map((item) => {
            const imageUrl = resolveDishGalleryImageUrl(item.imageUrl);
            const key = item.id || item.imageUrl;
            const caption = String(item.title || "Photo du plat").trim() || "Photo du plat";
            const editedTitle = editingTitles[key] ?? String(item.title || "").trim();

            return (
              <figure key={item.id || imageUrl} className="dish-gallery-admin-card">
                <img src={imageUrl} alt={caption} loading="lazy" />
                <figcaption>
                  <strong>{caption}</strong>
                  <label htmlFor={`dish-gallery-edit-title-${key}`}>Modifier le titre</label>
                  <input
                    id={`dish-gallery-edit-title-${key}`}
                    type="text"
                    value={editedTitle}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setEditingTitles((current) => ({
                        ...current,
                        [key]: nextValue,
                      }));
                    }}
                    placeholder="Titre de la photo"
                    disabled={loading}
                  />
                  <div className="dish-gallery-admin-card-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => updatePhotoTitle(item.imageUrl, editedTitle)}
                      disabled={loading}
                    >
                      Enregistrer le titre
                    </button>
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={() => removePhoto(item.imageUrl)}
                      disabled={loading}
                    >
                      Supprimer
                    </button>
                  </div>
                </figcaption>
              </figure>
            );
          })
        )}
      </section>
    </div>
  );
}

export default DishGalleryAdmin;